import { FunctionComponent, useEffect } from "react";

import { Modal } from "@/components";

import {
	useUnipileConnect,
	UnipileConnectType,
} from "@/pages/Private/pages/Account/hooks/useUnipileConnect";

interface ConnectAccountFlowProps {
	/** The account type to connect, or `null` while the flow is idle. */
	type: UnipileConnectType | null;
	onClose: () => void;
}

interface ConnectCopy {
	confirmTitle: string;
	confirmSubmit: string;
	confirmBody: string;
	openTabNote: string;
	validatingTitle: string;
	validatingBody: string;
}

const COPY: Record<UnipileConnectType, ConnectCopy> = {
	linkedin: {
		confirmTitle: "Login with your LinkedIn Account",
		confirmSubmit: "Start LinkedIn login",
		confirmBody: "Confirm this modal to login with LinkedIn.",
		openTabNote: "By clicking on the button, we will open a new tab to login with LinkedIn.",
		validatingTitle: "Currently logging in to LinkedIn",
		validatingBody:
			"Please wait while we validate your LinkedIn account. This may take a few seconds.",
	},
	email: {
		confirmTitle: "Connect your E-Mail Address",
		confirmSubmit: "Start E-Mail connection",
		confirmBody: "Confirm this modal to connect your E-Mail address.",
		openTabNote:
			"By clicking on the button, we will open a new tab to connect your E-Mail address.",
		validatingTitle: "Currently connecting your E-Mail Address",
		validatingBody:
			"Please wait while we validate your E-Mail address. This may take a few seconds.",
	},
};

/**
 * Drives the Unipile connect flow (same as the profile page) from a parent's
 * `type` state, so both the onboarding row CTA and the "Learn more" modal can
 * start it. Renders the confirm and validation modals.
 */
export const ConnectAccountFlow: FunctionComponent<ConnectAccountFlowProps> = ({
	type,
	onClose,
}) => {
	const activeType = type ?? "linkedin";

	const {
		isLoading,
		error,
		setError,
		showConfirmModal,
		setShowConfirmModal,
		startValidation,
		setStartValidation,
		connect,
	} = useUnipileConnect({ type: activeType });

	useEffect(() => {
		if (type) {
			setError(null);
			setShowConfirmModal(true);
		}
	}, [type, setError, setShowConfirmModal]);

	const copy = COPY[activeType];

	const closeConfirm = () => {
		setShowConfirmModal(false);
		onClose();
	};

	const closeValidation = () => {
		setStartValidation(false);
		onClose();
	};

	return (
		<>
			<Modal
				handleClose={!error ? closeConfirm : undefined}
				handleSave={() => {
					if (error) {
						setError(null);
						closeConfirm();
					} else {
						connect();
					}
				}}
				isLoading={isLoading}
				isOpened={showConfirmModal}
				size="xs"
				submitButtonText={error ? "Close" : copy.confirmSubmit}
				title={copy.confirmTitle}
			>
				{error && <div className="text-text-meta text-sm">{error}</div>}
				{!error && (
					<>
						<div className="text-text-meta text-sm">{copy.confirmBody}</div>
						<br />
						<div className="text-text-meta text-sm">
							{copy.openTabNote} <br />
							<span className="font-bold">Popups need to be allowed in your browser.</span>
						</div>
					</>
				)}
			</Modal>

			<Modal
				handleClose={closeValidation}
				handleSave={closeValidation}
				isOpened={startValidation}
				size="xs"
				submitButtonText="Done"
				title={copy.validatingTitle}
			>
				<div className="text-text-meta text-sm">{copy.validatingBody}</div>
			</Modal>
		</>
	);
};
